import type { MetricSortingConfig } from "../hooks/useMetricSorting"

/**
 * Default configuration for metric sorting
 * This can be customized as needed for different projects or views
 */
export const defaultMetricSortingConfig: MetricSortingConfig = {
  // Default category order
  categoryOrder: [
    "market",
    "financial",
    "valuation",
    "usage",
    "development",
    "technical",
    "custom",
    "other",
  ],

  // Custom order for metrics within categories
  // The order is defined by listing metric_ids in the desired order
  categoryMetricOrder: {
    // Example: Custom order for "market" category metrics
    market: [
      "price",
      "market_cap_fully_diluted",
      "market_cap_circulating",
      "token_trading_volume",
      "token_turnover_fully_diluted",
      "token_turnover_circulating",
      "tokenholders",
      "token_supply_maximum",
      "token_supply_circulating",
    ],

    financial: [
      "bridge_deposits",
      "tvl",
      "trading_volume",
      "transaction_volume",
      "notional_trading_volume",
      "transfer_volume",
      "assets_staked",
      "capital_deployed",
      "flash_loan_volume",
      "active_loans",
      "outstanding_supply",
      "fees",
      "fees_supply_side",
      "revenue",
      "expenses",
      "cost_of_revenue",
      "token_incentives",
      "operating_expenses",
      "gross_profit",
      "earnings",
      "treasury",
      "treasury_net",
    ],

    valuation: [
      "p_f_ratio_fully_diluted",
      "p_f_ratio_circulating",
      "p_s_ratio_fully_diluted",
      "p_s_ratio_circulating",
    ],

    usage: [
      "user_dau",
      "user_wau",
      "user_mau",
      "active_addresses_daily",
      "active_addresses_weekly",
      "active_addresses_monthly",
      "trading_volume_avg_per_trade_count",
      "trading_volume_avg_per_user",
      "afpu",
      "arpu",
      "gas_used",
      "transaction_count_contracts",
    ],

    development: [
      "active_developers",
      "code_commits",
      "contract_deployers",
      "contracts_deployed",
    ],

    technical: [
      "block_time",
      "transaction_count",
      "transactions_per_second",
      "transaction_fee_average",
      "transaction_fee_median",
    ],
    custom: [
      "trade_count",
      "tradeable_pairs",
      "tradeable_assets",
      "liquidity_turnover",
      "superchain_fees",
      "gdp",
      "ecosystem_tvl",
      "ecosystem_fees",
      "ecosystem_dex_trading_volume",
      "stablecoin_dau",
      "stablecoin_wau",
      "stablecoin_mau",
      "stablecoin_holders",
      "stablecoin_transfer_volume",
    ],

    // Add more categories as needed
  },
}

/**
 * Project-specific metric sorting configurations
 * This allows for different sorting configurations based on project type or other criteria
 */
const marketSectorSepcificConfigs: Record<string, MetricSortingConfig> = {
  // Example: Configuration for DeFi projects
  defi: {
    categoryOrder: [
      "financial",
      "market",
      "usage",
      "valuation",
      "development",
      "technical",
      "custom",
      "other",
    ],
    categoryMetricOrder: {
      financial: [
        // DeFi-specific financial metrics order
      ],
      // Other category orders...
    },
  },

  // Example: Configuration for NFT projects
  nft: {
    categoryOrder: [
      "market",
      "usage",
      "financial",
      "valuation",
      "development",
      "technical",
      "custom",
      "other",
    ],
    categoryMetricOrder: {
      market: [
        // NFT-specific market metrics order
      ],
      // Other category orders...
    },
  },

  // Add more project-specific configurations as needed
}

/**
 * Get the appropriate metric sorting configuration based on project type or other criteria
 */
export function getMetricSortingConfig(
  marketSectorId?: string
): MetricSortingConfig {
  if (marketSectorId && marketSectorSepcificConfigs[marketSectorId]) {
    return {
      ...defaultMetricSortingConfig,
      ...marketSectorSepcificConfigs[marketSectorId],
      categoryMetricOrder: {
        ...defaultMetricSortingConfig.categoryMetricOrder,
        ...marketSectorSepcificConfigs[marketSectorId].categoryMetricOrder,
      },
    }
  }

  return defaultMetricSortingConfig
}
